<template>
  <v-app>
    <v-app-bar color="primary" dark app>
      <v-toolbar-title>{{ $t('app.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      
      <!-- 语言切换按钮 -->
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            :title="$t('language.switch')"
          >
            <v-icon>mdi-translate</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            @click="switchLanguage('zh')"
            :class="{ 'primary--text': $i18n.locale === 'zh' }"
          >
            <v-list-item-title>{{ $t('language.chinese') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="switchLanguage('en')"
            :class="{ 'primary--text': $i18n.locale === 'en' }"
          >
            <v-list-item-title>{{ $t('language.english') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
    <v-main>
      <v-container>
        <v-row justify="center" class="mt-5">
          <v-col cols="12" md="6">
            <v-autocomplete
              ref="autocomplete"
              v-model="selectedItem"
              :items="filteredItems"
              :search-input.sync="search"
              :label="$t('search.inputPlaceholder')"
              item-text="text"
              item-value="id"
              @update:search-input="fetchAndFilterItems"
              :loading="loadingItems"
              outlined
              clearable
              prepend-inner-icon="mdi-magnify"
            >
            <template v-slot:item="data">
              <v-list-item 
              @click="selectItem(data.item.id)">
                <v-list-item-avatar>
                  <img :src="getItemIcon(data.item.icon)" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.text }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="2">
            <v-btn @click="fetchItemPrice" color="primary" :disabled="loadingPrice">{{ $t('common.search') }}</v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-progress-linear
              v-if="loadingPrice"
              indeterminate
              color="primary"
              class="mb-3"
            ></v-progress-linear>
          </v-col>
        </v-row>
        <v-row v-if="listings.length">
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="listings"
              :items-per-page="5"
              class="elevation-1"
            ></v-data-table>
          </v-col>
        </v-row>
        <v-alert v-if="listings.length === 0 && fetched" type="info">
          {{ $t('search.noResults') }}
        </v-alert>
        
        <!-- 右下角浮动按钮 -->
        <v-btn
          fab
          fixed
          bottom
          right
          color="primary"
          elevation="8"
          class="feedback-fab"
          @click="toggleFeedbackDrawer"
          :class="{ 'fab-active': feedbackDrawer }"
        >
          <transition name="icon-transition" mode="out-in">
            <v-icon large :key="feedbackDrawer ? 'close' : 'message'">
              {{ feedbackDrawer ? 'mdi-close' : 'mdi-message-text-outline' }}
            </v-icon>
          </transition>
        </v-btn>

        <!-- 滑动抽屉式表单 -->
        <v-navigation-drawer
          v-model="feedbackDrawer"
          fixed
          right
          temporary
          width="400"
          class="feedback-drawer"
          :class="{ 'drawer-open': feedbackDrawer }"
        >
          <div class="drawer-header">
            <div class="d-flex align-center justify-space-between pa-4">
              <div class="d-flex align-center">
                <v-icon color="primary" size="28" class="mr-3">mdi-message-text-outline</v-icon>
                <h2 class="text-h5 font-weight-bold">{{ $t('feedback.title') }}</h2>
              </div>
              <v-btn icon @click="toggleFeedbackDrawer" class="close-btn">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-divider></v-divider>
          </div>

          <div class="drawer-content pa-4">
            <p class="text-body-2 grey--text text--darken-1 mb-6">
              {{ $t('feedback.description') }}
            </p>

            <v-form ref="suggestionForm" v-model="suggestionFormValid">
              <!-- 意见内容输入区 -->
              <div class="mb-6">
                <div class="d-flex align-center mb-3">
                  <v-icon color="primary" class="mr-2">mdi-pencil</v-icon>
                  <label class="text-subtitle-1 font-weight-medium">{{ $t('feedback.contentLabel') }}</label>
                  <v-chip color="error" text-color="white" x-small class="ml-2">{{ $t('common.required') }}</v-chip>
                </div>
                <v-textarea
                  v-model="suggestion.content"
                  :placeholder="$t('feedback.contentPlaceholder')"
                  outlined
                  auto-grow
                  rows="4"
                  :rules="[
                    v => !!v || $t('feedback.validation.contentRequired'),
                    v => (v && v.length >= 10) || $t('feedback.validation.contentMinLength'),
                    v => (v && v.length <= 500) || $t('feedback.validation.contentMaxLength')
                  ]"
                  counter="500"
                  class="custom-textarea"
                  background-color="grey-lighten-5"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="grey" small class="mt-1">mdi-comment-text-outline</v-icon>
                  </template>
                </v-textarea>
              </div>

              <!-- 邮箱输入区 -->
              <div class="mb-6">
                <div class="d-flex align-center mb-3">
                  <v-icon color="primary" class="mr-2">mdi-email-outline</v-icon>
                  <label class="text-subtitle-1 font-weight-medium">{{ $t('feedback.emailLabel') }}</label>
                  <v-chip color="grey" text-color="white" x-small class="ml-2">{{ $t('common.optional') }}</v-chip>
                </div>
                <v-text-field
                  v-model="suggestion.userEmail"
                  :placeholder="$t('feedback.emailPlaceholder')"
                  outlined
                  :rules="[
                    v => !v || /.+@.+\..+/.test(v) || $t('feedback.validation.emailInvalid')
                  ]"
                  class="custom-textfield"
                  background-color="grey-lighten-5"
                  prepend-inner-icon="mdi-email-outline"
                >
                  <template v-slot:append>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          color="grey"
                          small
                        >
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>{{ $t('feedback.emailTooltip') }}</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </div>

              <!-- 提交按钮区域 -->
              <div class="d-flex justify-center mt-8">
                <v-btn
                  @click="submitSuggestion"
                  :loading="submittingSuggestion"
                  :disabled="!suggestionFormValid"
                  color="primary"
                  large
                  rounded
                  class="px-8 py-2 font-weight-medium submit-btn"
                  elevation="4"
                  block
                >
                  <v-icon left class="mr-2">mdi-send</v-icon>
                  {{ $t('feedback.submitButton') }}
                </v-btn>
              </div>
              
              <!-- 底部提示信息 -->
              <div class="text-center mt-6">
                <p class="text-caption grey--text">
                  <v-icon x-small class="mr-1">mdi-shield-check-outline</v-icon>
                  {{ $t('feedback.processingTime') }}
                </p>
              </div>
            </v-form>
          </div>
        </v-navigation-drawer>
        
        <!-- 优化后的提示信息 -->
        <v-snackbar
          v-model="showSnackbar"
          :color="snackbarColor"
          :timeout="4000"
          rounded="pill"
          elevation="8"
          transition="slide-y-reverse-transition"
          class="custom-snackbar"
        >
          <div class="d-flex align-center">
            <v-icon 
              :color="snackbarColor === 'success' ? 'white' : 'white'" 
              class="mr-3"
              size="24"
            >
              {{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <span class="font-weight-medium">{{ snackbarMessage }}</span>
          </div>
        </v-snackbar>
      </v-container>
    </v-main>
    <!-- <v-footer app>
      <v-col class="text-center">
        &copy; 2024 Your Company
      </v-col>
    </v-footer> -->
  </v-app>
</template>

<script>
import Papa from 'papaparse';

export default {
  data() {
    return {
      listings: [],
      fetched: false,
      loadingItems: false,
      loadingPrice: false,
      headers: [
        { text: this.$t('priceTable.headers.pricePerUnit'), value: 'pricePerUnit' },
        { text: this.$t('priceTable.headers.quantity'), value: 'quantity' },
        { text: this.$t('priceTable.headers.server'), value: 'worldName' },
        { text: this.$t('priceTable.headers.retainer'), value: 'retainerName' },
        { text: this.$t('priceTable.headers.total'), value: 'total' },
      ],
      items: [],
      filteredItems: [],
      search: '',
      selectedItem: null,
      // 意见提交相关数据
      suggestionFormValid: false,
      submittingSuggestion: false,
      suggestion: {
        content: '',
        userEmail: ''
      },
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      // 抽屉控制
      feedbackDrawer: false,
      // 设备检测
      isTouchDevice: false,
    };
  },
  methods: {
    async fetchAndFilterItems(search) {
      this.loadingItems = true;
      if (!this.items.length) {
        await this.fetchItems();
      }
      this.filterItems(search);
      this.loadingItems = false;
    },
    fetchItems() {
      return fetch(this.$api.endpoints.ITEMS_CSV)
        .then(response => response.text())
        .then(csvText => {
          Papa.parse(csvText, {
            header: true,
            complete: (results) => {
              const rows = results.data.slice(120);
              console.log(rows);
              this.items = rows
                .filter(row => row[22] == 'False')
                .map((row) => ({
                  id: row.key,
                  icon: row[10], // 假设 row[0] 是物品 ID
                  text: row[9], // 假设 row[1] 是物品名称
                }));
            },
          });
        });
    },
    filterItems(search) {
      if (search) {
        const lowerCaseSearch = search.toLowerCase();
        this.filteredItems = this.items.filter(item =>
          item.text?.toLowerCase()?.includes(lowerCaseSearch)
        );
      } else {
        this.filteredItems = this.items;
      }
    },
    padLeft(str, targetLength, padChar) {
      while (str.length < targetLength) {
        str = padChar + str;
      }
      return str;
    },
    getItemIcon(iconId) {
      let folderId;
      if (iconId.length >= 6) {
        iconId = this.padLeft(iconId, 6, '0');
        folderId = iconId.slice(0, 3) + "000";
      } else {
        iconId = "0" + this.padLeft(iconId, 5, '0');
        folderId = "0" + iconId.slice(1, 3) + "000";
      }
      return `https://xivapi.com/i/${folderId}/${iconId}.png`;
    },
    selectItem(id) {
      this.selectedItem = id;
      this.$refs.autocomplete.blur();  // Or this.$refs.autocomplete.menu.isActive = false;
    },
    async fetchItemPrice() {
      this.loadingPrice = true;
      this.fetched = false;
      try {
        const response = await fetch(`${this.$api.endpoints.ITEM_PRICE}?item=${encodeURIComponent(this.selectedItem)}`);
        const data = await response.json();
        this.listings = data.listings;
        this.fetched = true;
      } catch (error) {
        console.error('Error fetching item price:', error);
        this.fetched = true;
      }
      this.loadingPrice = false;
    },
    toggleFeedbackDrawer() {
      this.feedbackDrawer = !this.feedbackDrawer;
    },
    switchLanguage(locale) {
      // 动态导入setLocale函数
      import('@/locales').then(({ setLocale }) => {
        setLocale(locale);
        // 更新表格头部文本
        this.headers = [
          { text: this.$t('priceTable.headers.pricePerUnit'), value: 'pricePerUnit' },
          { text: this.$t('priceTable.headers.quantity'), value: 'quantity' },
          { text: this.$t('priceTable.headers.server'), value: 'worldName' },
          { text: this.$t('priceTable.headers.retainer'), value: 'retainerName' },
          { text: this.$t('priceTable.headers.total'), value: 'total' },
        ];
      });
    },
    async submitSuggestion() {
      if (!this.$refs.suggestionForm.validate()) return;
      
      this.submittingSuggestion = true;
      try {
        const response = await fetch(this.$api.endpoints.SUGGESTION, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: this.suggestion.content,
            userEmail: this.suggestion.userEmail || ''
          })
        });
        
        if (response.ok) {
          this.showSuccessMessage();
          this.resetSuggestionForm();
          // 提交成功后关闭抽屉
          setTimeout(() => {
            this.feedbackDrawer = false;
          }, 1500);
        } else {
          throw new Error('提交失败');
        }
      } catch (error) {
        console.error('Error submitting suggestion:', error);
        this.showErrorMessage();
      } finally {
        this.submittingSuggestion = false;
      }
    },
    resetSuggestionForm() {
      this.suggestion = {
        content: '',
        userEmail: ''
      };
      this.$refs.suggestionForm.resetValidation();
    },
    showSuccessMessage() {
      this.snackbarMessage = this.$t('feedback.messages.submitSuccess');
      this.snackbarColor = 'success';
      this.showSnackbar = true;
      
      // 添加成功后的视觉反馈
      this.$nextTick(() => {
        if (this.$refs.suggestionForm) {
          this.$refs.suggestionForm.reset();
        }
      });
    },
    showErrorMessage() {
      this.snackbarMessage = this.$t('feedback.messages.submitError');
      this.snackbarColor = 'error';
      this.showSnackbar = true;
      
      // 添加错误震动效果
      if (this.$refs.suggestionForm) {
        const formElement = this.$refs.suggestionForm.$el;
        formElement.classList.add('error-shake');
        setTimeout(() => {
          formElement.classList.remove('error-shake');
        }, 500);
      }
    },
  },
  mounted() {
    // 添加触摸设备检测
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },
};
</script>

<style scoped>
/* .v-application {
  background: linear-gradient(to right, #2196F3, #21CBF3);
} */

.v-autocomplete .v-input__control .v-input__append-inner {
  background-color: #FFFFFF;
}

.v-btn {
  margin-top: 8px;
}

/* 意见提交表单样式 */
.suggestion-card {
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(25, 118, 210, 0.1);
  position: relative;
}

/* 卡片顶部装饰 */
.card-header-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  overflow: hidden;
}

.decoration-pattern {
  height: 100%;
  background: linear-gradient(90deg, #1976D2, #42A5F5, #1976D2);
  background-size: 200% 100%;
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.suggestion-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

/* 自定义输入框样式 */
.custom-textarea .v-input__slot {
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.custom-textarea.v-input--is-focused .v-input__slot {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.custom-textfield .v-input__slot {
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.custom-textfield.v-input--is-focused .v-input__slot {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* 提交按钮样式 */
.v-btn--disabled {
  opacity: 0.6 !important;
}

/* 标签页动画 */
.v-chip {
  font-weight: 500;
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 标题区域图标动画 */
.v-icon.mdi-message-text-outline {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* 提交按钮悬停效果 */
.v-btn:not(.v-btn--disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(25, 118, 210, 0.3);
}

.v-btn:not(.v-btn--disabled):active {
  transform: translateY(0);
  box-shadow: 0 4px 8px rgba(25, 118, 210, 0.3);
}

/* 自定义提示条样式 */
.custom-snackbar .v-snack__content {
  padding: 0 24px;
}

/* 表单字段标签动画 */
.v-textarea:focus-within label,
.v-text-field:focus-within label {
  color: #1976D2;
}

/* 输入框计数器样式 */
.v-text-field .v-counter {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

/* 工具提示样式 */
.v-tooltip__content {
  border-radius: 8px;
  font-size: 0.875rem;
}

/* 错误震动效果 */
.error-shake {
  animation: shake 0.5s;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

/* 输入框错误状态样式 */
.v-input--has-state.error--text .v-input__slot {
  box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.2) !important;
}

/* 输入框成功状态样式 */
.v-input--has-state.success--text .v-input__slot {
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2) !important;
}

/* 提交按钮特殊样式 */
.submit-btn {
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  transition: all 0.3s ease;
}

.submit-btn:hover {
  background: linear-gradient(135deg, #1565C0, #2196F3);
}

/* 底部装饰元素 */
.bottom-decoration {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
}

.decoration-line {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #1976D2, transparent);
  margin-bottom: 12px;
}

.decoration-dots {
  display: flex;
  gap: 8px;
}

.decoration-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #1976D2;
  opacity: 0.6;
  animation: dotPulse 2s infinite;
}

.decoration-dots span:nth-child(2) {
  animation-delay: 0.5s;
}

.decoration-dots span:nth-child(3) {
  animation-delay: 1s;
}

@keyframes dotPulse {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

/* 浮动按钮样式 */
.feedback-fab {
  transition: all 0.3s ease;
  z-index: 1000;
}

.feedback-fab:hover {
  transform: scale(1.1);
  box-shadow: 0 8px 16px rgba(25, 118, 210, 0.3);
}

.feedback-fab.fab-active {
  transform: rotate(45deg);
  background-color: #f44336 !important;
}

/* 抽屉样式 */
.feedback-drawer {
  z-index: 1001;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
}

.feedback-drawer.v-navigation-drawer--is-mobile {
  width: 100% !important;
}

/* 抽屉头部样式 */
.drawer-header {
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  color: white;
}

.drawer-header .v-divider {
  border-color: rgba(255, 255, 255, 0.2);
}

.close-btn {
  color: white !important;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/* 抽屉内容样式 */
.drawer-content {
  height: calc(100vh - 80px);
  overflow-y: auto;
}

/* 抽屉打开时的动画 */
.drawer-open {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

/* 移动端适配 */
@media (max-width: 600px) {
  .feedback-drawer {
    width: 100% !important;
  }
  
  .drawer-content {
    padding: 16px !important;
  }
  
  .feedback-fab {
    bottom: 16px !important;
    right: 16px !important;
    width: 56px !important;
    height: 56px !important;
  }
  
  .feedback-fab .v-icon {
    font-size: 28px !important;
  }
}

/* 触摸设备优化 */
@media (hover: none) and (pointer: coarse) {
  .feedback-fab {
    width: 60px !important;
    height: 60px !important;
  }
  
  .feedback-fab .v-icon {
    font-size: 32px !important;
  }
  
  .custom-textarea .v-input__slot,
  .custom-textfield .v-input__slot {
    min-height: 56px !important;
  }
  
  .submit-btn {
    min-height: 48px !important;
    font-size: 1rem !important;
  }
}

/* 浮动按钮脉冲动画 */
.feedback-fab .v-icon {
  animation: fabPulse 2s infinite;
}

/* 图标过渡动画 */
.icon-transition-enter-active,
.icon-transition-leave-active {
  transition: all 0.3s ease;
}

.icon-transition-enter,
.icon-transition-leave-to {
  opacity: 0;
  transform: rotate(180deg) scale(0.5);
}

@keyframes fabPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* 抽屉表单优化 */
.feedback-drawer .custom-textarea,
.feedback-drawer .custom-textfield {
  margin-bottom: 16px;
}

.feedback-drawer .submit-btn {
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  transition: all 0.3s ease;
}

.feedback-drawer .submit-btn:hover {
  background: linear-gradient(135deg, #1565C0, #2196F3);
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(25, 118, 210, 0.3);
}
</style>
